import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { apiUrl } from '../config/api';
import { APP_NAME } from '../config/branding';
import AppDateField from '../components/inputs/AppDateField';
import {
  formatSriLankaDateLabel,
  getSriLankaCurrentMonth,
  getSriLankaCurrentYear,
} from '../lib/sriLankaTime';

const FUNDS_API = apiUrl('organization_funds');
const STAFF_API = apiUrl('get_staff');

const SOURCE_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'penalty_deduction', label: 'Penalty deduction' },
  { value: 'punishment_half_day', label: 'Punishment half-day balance' },
  { value: 'tips_withheld', label: 'Tips removed from salary' },
  { value: 'service_withheld', label: 'Service share removed from salary' },
  { value: 'service_charge_org_50', label: 'Service charge share (50%)' },
];

function fmtMoney(value) {
  return Number(value || 0).toFixed(2);
}

function sourceColor(sourceType) {
  if (sourceType === 'penalty_deduction') return 'warning';
  if (sourceType === 'punishment_half_day') return 'warning';
  if (sourceType === 'tips_withheld') return 'secondary';
  if (sourceType === 'service_withheld') return 'info';
  if (sourceType === 'service_charge_org_50') return 'success';
  return 'default';
}

function sourceLabel(sourceType, fallbackLabel = '') {
  if (fallbackLabel) return fallbackLabel;
  const hit = SOURCE_OPTIONS.find((s) => s.value === sourceType);
  return hit?.label || sourceType || 'Unknown';
}

function formatDate(dateText) {
  if (!dateText) return '-';
  return formatSriLankaDateLabel(dateText);
}

export default function OrganizationFundPage() {
  const [year, setYear] = useState(() => getSriLankaCurrentYear());
  const [month, setMonth] = useState(() => getSriLankaCurrentMonth());
  const [sourceType, setSourceType] = useState('');
  const [staffId, setStaffId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [staffList, setStaffList] = useState([]);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({
    penalty_deduction: 0,
    punishment_half_day: 0,
    tips_withheld: 0,
    service_withheld: 0,
    service_charge_org_50: 0,
    total: 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchStaff = useCallback(async () => {
    try {
      const res = await fetch(STAFF_API);
      const data = await res.json();
      setStaffList(Array.isArray(data) ? data : []);
    } catch {
      setStaffList([]);
    }
  }, []);

  const fetchFunds = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      if (Number(year) > 0) params.set('year', String(year));
      if (Number(month) >= 1 && Number(month) <= 12) params.set('month', String(month));
      if (sourceType) params.set('source_type', sourceType);
      if (staffId !== '') params.set('staff_id', String(staffId));
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);

      const res = await fetch(`${FUNDS_API}?${params.toString()}`);
      const data = await res.json();

      if (data?.status !== 'ok') {
        throw new Error(data?.message || 'Failed to load organization funds');
      }

      const nextItems = Array.isArray(data?.items) ? data.items : [];
      const nextTotals = {
        penalty_deduction: Number(data?.totals?.penalty_deduction || 0),
        punishment_half_day: Number(data?.totals?.punishment_half_day || 0),
        tips_withheld: Number(data?.totals?.tips_withheld || 0),
        service_withheld: Number(data?.totals?.service_withheld || 0),
        service_charge_org_50: Number(data?.totals?.service_charge_org_50 || 0),
        total: Number(data?.totals?.total || 0),
      };

      setItems(nextItems);
      setTotals(nextTotals);
    } catch (err) {
      setError(err.message || 'Network error');
      setItems([]);
      setTotals({
        penalty_deduction: 0,
        punishment_half_day: 0,
        tips_withheld: 0,
        service_withheld: 0,
        service_charge_org_50: 0,
        total: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [year, month, sourceType, staffId, dateFrom, dateTo]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  useEffect(() => {
    fetchFunds();
  }, [fetchFunds]);

  const summaryCards = useMemo(() => ([
    {
      key: 'penalty_deduction',
      title: 'Penalty Fund In',
      amount: totals.penalty_deduction,
      color: 'warning.main',
    },
    {
      key: 'punishment_half_day',
      title: 'Punish Half In',
      amount: totals.punishment_half_day,
      color: 'warning.dark',
    },
    {
      key: 'tips_withheld',
      title: 'Tips Removed In',
      amount: totals.tips_withheld,
      color: 'secondary.main',
    },
    {
      key: 'service_withheld',
      title: 'No-Service Share In',
      amount: totals.service_withheld,
      color: 'info.main',
    },
    {
      key: 'service_charge_org_50',
      title: 'Service 50% In',
      amount: totals.service_charge_org_50,
      color: 'success.main',
    },
    {
      key: 'total',
      title: 'Total Fund',
      amount: totals.total,
      color: 'primary.main',
    },
  ]), [totals]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <AccountBalanceIcon color="primary" />
        <Typography variant="h4">{APP_NAME} Organization Fund</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Funds collected from salary penalties, punishment half-day balances, no-tips/no-service-share deductions, and 50% service charge share.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ flexWrap: 'wrap' }}>
          <TextField
            label="Year"
            type="number"
            size="small"
            value={year}
            onChange={(e) => setYear(Number(e.target.value || 0))}
            sx={{ minWidth: 120 }}
          />
          <TextField
            label="Month"
            type="number"
            size="small"
            value={month}
            inputProps={{ min: 1, max: 12 }}
            onChange={(e) => setMonth(Number(e.target.value || 0))}
            sx={{ minWidth: 120 }}
          />
          <TextField
            select
            label="Source"
            size="small"
            value={sourceType}
            onChange={(e) => setSourceType(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {SOURCE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Staff"
            size="small"
            value={staffId}
            onChange={(e) => setStaffId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All Staff + Organization</MenuItem>
            <MenuItem value="0">Organization</MenuItem>
            {staffList.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
            ))}
          </TextField>
          <AppDateField
            label="From"
            size="small"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            helperText="DD/MM/YYYY"
          />
          <AppDateField
            label="To"
            size="small"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            helperText="DD/MM/YYYY"
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchFunds}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setSourceType('');
                setStaffId('');
                setDateFrom('');
                setDateTo('');
              }}
              disabled={loading}
            >
              Clear Filters
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {!!error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        {summaryCards.map((card) => (
          <Card key={card.key} sx={{ flex: 1, minWidth: 180 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                {card.title}
              </Typography>
              <Typography variant="h5" sx={{ color: card.color, fontWeight: 700 }}>
                Rs {fmtMoney(card.amount)}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Paper sx={{ overflowX: 'auto' }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">No organization fund records found for the selected filters.</Alert>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Staff</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="right">Amount (Rs)</TableCell>
                <TableCell>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{formatDate(row.fund_date)}</TableCell>
                  <TableCell>{row.year}-{String(row.month || '').padStart(2, '0')}</TableCell>
                  <TableCell>{row.staff_name || (Number(row.staff_id || 0) === 0 ? 'Organization' : '-') }</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={sourceLabel(row.source_type, row.source_label)}
                      color={sourceColor(row.source_type)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Rs {fmtMoney(row.amount)}</TableCell>
                  <TableCell>{row.note || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
